<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toast-notification";

import missionService from "@/modules/companyNeed/services/missionService";

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
// Import components
import Modal from "@/dsComponents/modals/Modal.vue";

interface ValidateMissionProps {
    open: boolean;
    missionId: string;
    /** Called once the command succeeded. */
    onSuccess?: () => void;
    /** Applies the validation optimistically and returns its rollback. */
    onApply?: () => () => void;
}

const props = defineProps<ValidateMissionProps>();
const { t } = useI18n();

const emit = defineEmits<(e: "close") => void>();

const toaster = useToast();

const loading = ref(false);

const handleClose = () => {
    emit("close");
};

const validateMissionConfirm = async () => {
    let rollback: (() => void) | undefined;
    try {
        loading.value = true;
        rollback = props.onApply?.();
        await missionService.validateMission(props.missionId);
        rollback = undefined;

        toaster?.open({
            message: t("page.companyNeed.missions.validateModal.success"),
            type: "success",
            duration: 5000,
        });

        props.onSuccess?.();
        emit("close");
    } catch (err) {
        rollback?.();
        console.error("Error validating mission:", err);

        toaster?.open({
            message: t("page.companyNeed.missions.validateModal.error"),
            type: "error",
            duration: 5000,
        });
        emit("close");
    } finally {
        loading.value = false;
    }
};
</script>
<template>
    <Modal :open="open" :title="t('page.companyNeed.missions.validateModal.title')" width="500px" @close="handleClose">
        <template #default>
            <div class="validate-mission-modal-content">
                <span class="modal-description typo-sm-regular">{{ t("page.companyNeed.missions.validateModal.description") }}</span>
            </div>
        </template>
        <template #footer>
            <div class="button-container">
                <ButtonAlt type="neutral" variant="ghost" :label="t('global.actions.cancel')" @click="handleClose()" :loading="loading" />
                <ButtonAlt
                    type="primary"
                    variant="filled"
                    @click="validateMissionConfirm()"
                    :label="t('page.companyNeed.missions.validateModal.action')"
                    :loading="loading"
                />
            </div>
        </template>
    </Modal>
</template>
<style scoped lang="scss">
.button-container {
    gap: var(--spacing-lg, 12px);
    display: flex;
    align-items: center;
    justify-content: flex-end;
}

.validate-mission-modal-content {
    gap: var(--spacing-xl, 16px);
    display: flex;
    flex-direction: column;
}
</style>
